import React from "react";
import Skeleton from "../common/skeleton";
import SectionError from "./section_error";

const formatKrwSigned = (n) => {
    const v = Number(n) || 0;
    return (v >= 0 ? "+₩" : "-₩") + Math.abs(Math.round(v)).toLocaleString();
};

/**
 * FxBreakdownStrip — ③ 환율 분해 스트립.
 * 총 손익 = 주가수익(stock_pnl_krw) + 환차익(fx_pnl_krw). 비례 바는 절대값 비율로
 * 폭을 나누고, 각 세그먼트 색은 자신의 부호(gain/loss)를 따른다.
 */
function FxBreakdownStrip({ summary, loading, error, onRetry }) {
    if (loading) {
        return (
            <div className="rounded-2xl border border-surface-border bg-surface-card p-4 shadow-card">
                <Skeleton variant="line" width="30%" height={14} />
                <Skeleton variant="rect" height={12} className="mt-3" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="rounded-2xl border border-surface-border bg-surface-card p-4 shadow-card">
                <SectionError message="환율 분해 정보를 불러오지 못했습니다." onRetry={onRetry} />
            </div>
        );
    }

    const s = summary || {};
    const stockPnl = Number(s.stock_pnl_krw) || 0;
    const fxPnl = Number(s.fx_pnl_krw) || 0;
    const totalPnl = stockPnl + fxPnl;
    const totalAbs = Math.abs(stockPnl) + Math.abs(fxPnl);
    const stockRatio = totalAbs > 0 ? (Math.abs(stockPnl) / totalAbs) * 100 : 50;
    const fxRatio = totalAbs > 0 ? 100 - stockRatio : 50;

    return (
        <div className="rounded-2xl border border-surface-border bg-surface-card p-4 shadow-card">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm font-semibold text-content-secondary">환율 분해</div>
                <div className={`font-num text-sm font-semibold ${totalPnl >= 0 ? "text-gain" : "text-loss"}`}>
                    총손익 {formatKrwSigned(totalPnl)}
                </div>
            </div>

            {totalAbs === 0 ? (
                <div className="mt-4 h-3 w-full rounded-full bg-surface-elevated" role="img" aria-label="손익 없음" />
            ) : (
                <div className="mt-4 flex h-3 w-full overflow-hidden rounded-full bg-surface-elevated" role="img" aria-label="주가수익/환차익 비례 바">
                    <div
                        className={stockPnl >= 0 ? "bg-gain" : "bg-loss"}
                        style={{ width: `${stockRatio}%` }}
                        title={`주가수익 ${formatKrwSigned(stockPnl)}`}
                    />
                    <div
                        className={fxPnl >= 0 ? "bg-gain/60" : "bg-loss/60"}
                        style={{ width: `${fxRatio}%` }}
                        title={`환차익 ${formatKrwSigned(fxPnl)}`}
                    />
                </div>
            )}

            <div className="mt-3 grid grid-cols-2 gap-2 sm:gap-3">
                <div className="rounded-xl border border-surface-border bg-surface-card-alt px-3 py-2.5">
                    <div className="flex items-center gap-1.5 text-xs text-content-muted">
                        <span className={`inline-block h-2 w-2 rounded-full ${stockPnl >= 0 ? "bg-gain" : "bg-loss"}`} aria-hidden="true" />
                        주가수익
                    </div>
                    <div className={`mt-1 font-num text-base font-semibold ${stockPnl >= 0 ? "text-gain" : "text-loss"}`}>
                        {formatKrwSigned(stockPnl)}
                    </div>
                </div>
                <div className="rounded-xl border border-surface-border bg-surface-card-alt px-3 py-2.5">
                    <div className="flex items-center gap-1.5 text-xs text-content-muted">
                        <span className={`inline-block h-2 w-2 rounded-full ${fxPnl >= 0 ? "bg-gain/60" : "bg-loss/60"}`} aria-hidden="true" />
                        환차익
                    </div>
                    <div className={`mt-1 font-num text-base font-semibold ${fxPnl >= 0 ? "text-gain" : "text-loss"}`}>
                        {formatKrwSigned(fxPnl)}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default FxBreakdownStrip;
